"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const apollo_client_1 = __importDefault(require("apollo-client"));
const apollo_link_http_1 = require("apollo-link-http");
const apollo_cache_inmemory_1 = require("apollo-cache-inmemory");
const queries_1 = require("./queries");
class OffchainEndpointManager {
    constructor() {
        // Get the param:
        // 1. ?endpoint=thegraph for thegraph
        // 2. ?endpoint=ibexer for ponder
        // 3. none for both and picks the best of the 2
        this.endpoint = null;
        // https://stackoverflow.com/questions/32598971/whats-the-purpose-of-if-typeof-window-undefined
        this.endpoint = typeof window !== 'undefined' ? this.getPreferredEndpoint(window.location.search) : null;
    }
    // ---------------- Getter ---------------- //
    /**
     * Main fetcher function to query from multiple endpoints. The query MUST have _meta included.
     *
     * @param endpoints - Array of endpoints
     * @param network - The network of the request (needed for the block due to ponder's `_meta` result)
     * @param queryBuilder - The query requested from IQueryBuilder interface (ie. `lendingPoolQuery`, `tvlQuery`, etc.)
     */
    fetch(endpoints, network, queryBuilder) {
        return __awaiter(this, void 0, void 0, function* () {
            // This is a test as we roll this out, to filter pass URL search param with thegraph or ibexer
            const filteredEndpoints = this.filterEndpoints(endpoints);
            if (filteredEndpoints.length === 0) {
                console.warn("No endpoints available after filtering");
                return null;
            }
            const endpointQueries = filteredEndpoints.map(endpoint => ({
                endpoint,
                query: queryBuilder(queries_1.QueryBuilderFactory.getQuery(endpoint)),
            }));
            const endpointResults = yield Promise.allSettled(endpointQueries.map(eq => this.fetchFromEndpoint(eq)));
            // If one endpoint breaks then it shouldnt break, filter it out and report
            const responses = endpointResults
                .filter((result, index) => {
                if (result.status === "rejected") {
                    console.log(`${endpointQueries[index].endpoint} is down?`);
                }
                return result.status === 'fulfilled';
            })
                .map(result => result.value);
            const latestResponse = this.bestEndpointBlock(responses, network);
            if (!latestResponse) {
                console.error("Cant find endpoint block");
                return null;
            }
            // Transform the data if needed in case of ponder or different api responses
            return queries_1.QueryBuilderFactory.transformData(latestResponse.result);
        });
    }
    // ---------------- Private ---------------- //
    /**
     * Fetch a query from an endpoint (ponder, thegraph, etc.)
     */
    fetchFromEndpoint({ endpoint, query }) {
        return __awaiter(this, void 0, void 0, function* () {
            const client = new apollo_client_1.default({
                link: new apollo_link_http_1.HttpLink({ uri: endpoint }),
                cache: new apollo_cache_inmemory_1.InMemoryCache()
            });
            const result = yield client.query({
                query,
                fetchPolicy: 'cache-first'
            });
            return { endpoint, result };
        });
    }
    /**
     * Given an array of responses from endpoint query, get the response wiht the latest block number
     */
    bestEndpointBlock(responses, network) {
        if (!responses.length)
            return null;
        let bestEndpoint = responses[0];
        let maxBlockNumber = 0;
        for (const response of responses) {
            const queryBuilder = queries_1.QueryBuilderFactory.getQuery(response.endpoint);
            const blockNumber = queryBuilder.getBlockNumber(response.result, network);
            if (blockNumber > maxBlockNumber && response.result.data) {
                maxBlockNumber = blockNumber;
                bestEndpoint = response;
            }
        }
        return Object.assign(Object.assign({}, bestEndpoint), { maxBlockNumber });
    }
    // ---------------- Monitoring ---------------- //
    filterEndpoints(endpoints) {
        // No endpoint passed, ignore
        if (!this.endpoint)
            return endpoints;
        const preferredEndpoint = this.endpoint === "thegraph" ? "thegraph" : "railway";
        return endpoints.filter(endpoint => endpoint.includes(preferredEndpoint));
    }
    getPreferredEndpoint(searchParams) {
        const urlParams = new URLSearchParams(searchParams);
        const endpoint = urlParams.get("endpoint");
        if (!endpoint)
            return null;
        switch (endpoint.toLowerCase()) {
            case "thegraph":
                return "thegraph";
            case "ibexer":
                return "ibexer";
            default:
                return null;
        }
    }
}
exports.default = OffchainEndpointManager;
